import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Camera, ImageOff, Images, Play } from "lucide-react";
import { API, GALLERY_CATEGORIES, gallerySrc, videoSrc } from "@/config/site";
import { usePageTitle, Reveal, SectionHead, Eyebrow } from "@/components/bits";
import Lightbox from "@/components/Lightbox";
import BeforeAfter from "@/components/BeforeAfter";

export default function OurWork() {
  usePageTitle("Our Work");
  const [filter, setFilter] = useState("All");
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["gallery"],
    queryFn: async () => (await axios.get(`${API}/gallery`)).data,
  });

  const filtered = useMemo(
    () => (filter === "All" ? items : items.filter((i) => i.category === filter)),
    [items, filter]
  );

  const cards = useMemo(() => {
    const list = [];
    filtered.forEach((item) => {
      if (item.is_before_after) return;
      if (item.is_video && item.video_path) {
        list.push({ video: true, item, src: videoSrc(item) });
      } else if (item.is_album && (item.album_paths || []).length) {
        list.push({ album: true, item, cover: gallerySrc(item), count: item.album_paths.length });
      } else if (item.image_path || item.image_url) {
        list.push({ album: false, item, src: gallerySrc(item) });
      }
    });
    return list;
  }, [filtered]);

  const flat = useMemo(
    () =>
      cards.flatMap((c) => {
        if (c.video) return [{ src: c.src, title: c.item.title, video: true }];
        if (c.album) return c.item.album_paths.map((p, pi) => ({ src: `${API}/files/${p}`, title: `${c.item.title} (${pi + 1} of ${c.count})` }));
        return [{ src: c.src, title: c.item.title }];
      }),
    [cards]
  );

  const starts = useMemo(() => {
    let acc = 0;
    return cards.map((c) => {
      const s = acc;
      acc += c.album ? c.count : 1;
      return s;
    });
  }, [cards]);

  return (
    <>
      <section className="border-b border-white/10 bg-joell-coal/40" data-testid="our-work-hero">
        <div className="container-x pb-14 pt-36 md:pb-16 md:pt-44">
          <Reveal>
            <Eyebrow>Portfolio</Eyebrow>
            <h1 className="mt-4 font-display text-4xl font-bold uppercase tracking-tight text-white sm:text-6xl">Our Work</h1>
            <p className="mt-4 max-w-2xl text-base text-slate-300 sm:text-lg">
              A look at completed projects showcasing our workmanship, attention to detail and range of services.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="container-x py-12 md:py-16" data-testid="our-work-gallery-section">
        <Reveal>
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Project categories" data-testid="gallery-filters">
            {GALLERY_CATEGORIES.map((c) => (
              <button
                key={c}
                role="tab"
                aria-selected={filter === c}
                onClick={() => setFilter(c)}
                className={`border px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.15em] transition-colors ${
                  filter === c
                    ? "border-joell-yellow bg-joell-yellow text-joell-black"
                    : "border-white/15 text-slate-400 hover:border-white/40 hover:text-white"
                }`}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        {isLoading ? (
          <p className="mt-14 font-mono text-xs uppercase tracking-widest text-slate-500">Loading projects…</p>
        ) : (
          <>
            {cards.length === 0 && filtered.filter((i) => i.is_before_after).length === 0 && (
              <div className="mt-14 flex flex-col items-center gap-3 border border-dashed border-white/20 p-14 text-center" data-testid="gallery-empty-state">
                <ImageOff size={28} className="text-joell-yellow" />
                <p className="font-mono text-xs uppercase tracking-widest text-slate-500">
                  {filter === "All" ? "Project photos coming soon" : `No ${filter} projects yet`}
                </p>
              </div>
            )}

            {filtered.some((i) => i.is_before_after) && (
              <div className="mt-12 grid gap-8 md:grid-cols-2">
                {filtered.filter((i) => i.is_before_after).map((item, i) => (
                  <Reveal key={item.id} delay={i * 0.06}>
                    <div>
                      <BeforeAfter
                        beforeSrc={gallerySrc(item, "before")}
                        afterSrc={gallerySrc(item, "after")}
                        title={item.title}
                      />
                      <div className="mt-3 flex items-center justify-between">
                        <span className="font-display text-sm font-semibold uppercase tracking-wide text-white">{item.title}</span>
                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-joell-yellow">{item.category}</span>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}

            {cards.length > 0 && (
              <div className="mt-12 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
                {cards.map((c, i) => (
                  <Reveal key={c.item.id} delay={i * 0.04}>
                    <button
                      onClick={() => setLightboxIdx(starts[i])}
                      className="group relative block w-full aspect-[4/3] overflow-hidden border border-white/10 text-left"
                      data-testid={c.video ? `gallery-video-${i}` : c.album ? `gallery-album-${i}` : `gallery-item-${i}`}
                      aria-label={c.video ? `Play video: ${c.item.title}` : c.album ? `Open album: ${c.item.title}` : `Enlarge photo: ${c.item.title}`}
                    >
                      {c.video ? (
                        <video
                          src={c.src}
                          muted
                          playsInline
                          preload="metadata"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <img
                          src={c.album ? c.cover : c.src}
                          alt={c.item.title}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-joell-black/85 via-transparent to-transparent" />
                      {(c.album || c.video) && (
                        <span className="absolute left-3 top-3 flex items-center gap-1.5 bg-joell-yellow px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-joell-black">
                          {c.video ? <Play size={11} /> : <Images size={11} />} {c.video ? "Video" : `${c.count} photos`}
                        </span>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between gap-2 p-4">
                        <span className="font-display text-sm font-semibold uppercase tracking-wide text-white">{c.item.title}</span>
                        <Camera size={15} className="shrink-0 text-joell-yellow opacity-0 transition-opacity group-hover:opacity-100" />
                      </div>
                    </button>
                  </Reveal>
                ))}
              </div>
            )}
          </>
        )}

        <p className="mt-16 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
          New projects are added regularly — tap any photo to enlarge
        </p>
      </section>

      <Lightbox
        items={flat}
        index={lightboxIdx}
        onIndex={setLightboxIdx}
        onClose={() => setLightboxIdx(-1)}
      />
    </>
  );
}
